/**
 * Roles in a studio draft (decision 4): drafted and built with the
 * workspace, people as names only, invited after Build (board
 * `canvas-inspect-role` 5.12). The rules mirror the team routes'
 * (`apps/server/src/team-rules.ts`): a role holds together, and a record at
 * Change needs the role to add or change entries; admins open everything and
 * are never given access.
 *
 * Row-level reach ("only home deliveries") is not in RecordOS: the agent
 * records it as a not-yet item with the closest fit, never as a role part.
 */
import { enFlat } from '../i18n/en';
import {
  allPermissions,
  hasReach,
  isOn,
  isReach,
  memberPermissions,
  PERMISSION_KEYS,
  unmetNeeds,
  viewerPermissions,
  type PermissionKey,
  type RolePermissions,
} from '../roles';
import { STUDIO_LIMITS, type StudioDraft, type StudioRole } from './draft';
import type { StudioProblem } from './judge';

const NAME_MAX = 60;
const PURPOSE_MAX = 500;

const PRESETS: Readonly<Record<StudioRole['base'], () => RolePermissions>> = {
  admin: allPermissions,
  member: memberPermissions,
  viewer: viewerPermissions,
};

/** The role's permissions: its base preset, then each pair laid over it. A pair naming no permission is skipped. */
export const studioRolePermissions = (role: StudioRole): RolePermissions => {
  const permissions = { ...PRESETS[role.base]() } as Record<string, unknown>;
  for (const { key, value } of role.permissions) {
    if ((PERMISSION_KEYS as readonly string[]).includes(key)) permissions[key] = value;
  }
  return permissions as RolePermissions;
};

/** The names every workspace's roles are made with, which a drafted role never takes. */
const MADE_WITH = (): string[] =>
  (['authority.admin', 'authority.member', 'authority.viewer'] as const).map((key) => (enFlat[key] ?? '').toLowerCase());

/** Every problem with one role, all at once. Path and part are `role:<key>`. */
export const roleProblems = (role: StudioRole, draft: StudioDraft): StudioProblem[] => {
  const path = `role:${role.key}`;
  const problems: StudioProblem[] = [];
  const say = (code: string, message: string): void => {
    problems.push({ code, message, path, part: path });
  };

  const name = role.name.trim();
  if (name.length === 0 || name.length > NAME_MAX) say('ROLE_NAME', `A role is named in 1 to ${NAME_MAX} characters.`);
  const lower = name.toLowerCase();
  if (
    name.length > 0 &&
    (draft.roles.some((other) => other.key !== role.key && other.name.trim().toLowerCase() === lower) || MADE_WITH().includes(lower))
  ) {
    say('ROLE_NAME_TAKEN', `"${name}" is already a role's name. Give this one a name of its own.`);
  }
  if (role.purpose !== undefined && role.purpose.length > PURPOSE_MAX) say('ROLE_PURPOSE', `A role's purpose is ${PURPOSE_MAX} characters at most.`);
  if (role.people.length > STUDIO_LIMITS.rolePeople || role.people.some((person) => person.trim().length === 0 || person.trim().length > NAME_MAX)) {
    say('ROLE_PEOPLE', `A role names ${STUDIO_LIMITS.rolePeople} people at most, each in 1 to ${NAME_MAX} characters.`);
  }

  const seen = new Set<string>();
  for (const { key, value } of role.permissions) {
    if (!(PERMISSION_KEYS as readonly string[]).includes(key)) {
      say('ROLE_PERMISSION', `"${key}" is not a permission.`);
      continue;
    }
    if (seen.has(key)) say('ROLE_PERMISSION', `"${key}" is set twice.`);
    seen.add(key);
    if (hasReach(key as PermissionKey) ? !isReach(value) : typeof value !== 'boolean') {
      say('ROLE_PERMISSION', `"${key}" takes ${hasReach(key as PermissionKey) ? 'off, own, team or everyone' : 'true or false'}.`);
    }
  }

  const permissions = studioRolePermissions(role);
  const unmet = unmetNeeds(permissions);
  if (unmet.length > 0) {
    say('ROLE_NEEDS', `Switch on what these need first: ${unmet.map((one) => `${one.key} (${one.needs.join(', ')})`).join('; ')}.`);
  }

  const granted = new Set<string>();
  for (const row of role.access) {
    if (!draft.records.some((record) => record.key === row.record)) {
      say('ROLE_RECORD_UNKNOWN', `"${row.record}" is not a record or list of this draft.`);
    }
    if (granted.has(row.record)) say('ROLE_ACCESS_TWICE', `"${row.record}" is given twice.`);
    granted.add(row.record);
  }
  if (role.base === 'admin' && role.access.length > 0) {
    say('ROLE_ADMIN_OPENS_ALL', 'An admin opens everything, so it is never given access record by record.');
  }
  const writes = isOn(permissions, 'add_entries') || isOn(permissions, 'change_entries');
  if (!writes && role.access.some((row) => row.level === 'change')) {
    say('ROLE_CHANGE_NEEDS_WRITE', 'A record at Change needs the role to add or change entries.');
  }
  return problems;
};
